// src/components/backoffice/articles/ArticlesListPage.tsx

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { articlesService, Article } from '../../../services/backoffice/articlesService';

// הרשימה נמשכת פעם אחת ומסוננת בדפדפן.
// ה-API של הרשימה לא מחזיר את שדה content, ולכן גם עשרות מאמרים הם מטען קטן,
// והחיפוש יוצא מיידי בלי סיבובי רשת. אם מספר המאמרים יתקרב ל-FETCH_LIMIT —
// להעביר חיפוש, סינון ועימוד לצד השרת (דורש תמיכה חדשה ב-Api.js).
const FETCH_LIMIT = 500;
const PAGE_SIZE = 20;

type StatusFilter = 'all' | 'published' | 'draft';

const ArticlesListPage: React.FC = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [page, setPage] = useState(1);

  const loadArticles = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await articlesService.getArticles({ limit: FETCH_LIMIT });
      setArticles(data.articles);
    } catch (err: any) {
      setError(`שגיאה בטעינת מאמרים: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadArticles(); }, [loadArticles]);

  useEffect(() => {
    if (successMessage) {
      const t = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(t);
    }
  }, [successMessage]);

  const publishedCount = useMemo(
    () => articles.filter((a) => a.is_published).length,
    [articles]
  );

  const categories = useMemo(() => {
    const names = articles
      .map((a) => a.category_name)
      .filter((name): name is string => !!name);
    return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b, 'he'));
  }, [articles]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return articles.filter((article) => {
      if (statusFilter === 'published' && !article.is_published) return false;
      if (statusFilter === 'draft' && article.is_published) return false;
      if (categoryFilter && article.category_name !== categoryFilter) return false;
      if (!term) return true;
      const haystack = `${article.title} ${article.excerpt || ''} ${article.category_name || ''}`;
      return haystack.toLowerCase().includes(term);
    });
  }, [articles, search, statusFilter, categoryFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  // סינון שמצמצם את התוצאות עלול להשאיר אותנו בעמוד שכבר לא קיים
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const pageItems = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page]
  );

  const rangeStart = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, filtered.length);
  const isFiltered = !!search.trim() || statusFilter !== 'all' || !!categoryFilter;

  const resetFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setCategoryFilter('');
    setPage(1);
  };

  const handleDelete = async (id: number) => {
    try {
      setDeleting(true);
      await articlesService.deleteArticle(id);
      setSuccessMessage('המאמר נמחק בהצלחה');
      setDeleteConfirmId(null);
      await loadArticles();
    } catch (err: any) {
      setError(`שגיאה במחיקה: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const selectClass =
    'border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div className="p-6" dir="rtl">
      <div className="flex items-center justify-between mb-2 gap-4 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-800">ניהול מאמרים</h1>
        <div className="flex gap-3">
          <Link
            to="/backoffice/articles/categories"
            className="px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors text-sm"
          >
            ניהול קטגוריות
          </Link>
          <Link
            to="/backoffice/articles/new"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
          >
            + מאמר חדש
          </Link>
        </div>
      </div>

      {!loading && !error && (
        <p className="text-sm text-gray-500 mb-6">
          {articles.length} מאמרים · {publishedCount} מפורסמים ·{' '}
          {articles.length - publishedCount} טיוטות
        </p>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
          {successMessage}
        </div>
      )}

      {/* חיפוש וסינון */}
      {!loading && articles.length > 0 && (
        <div className="bg-white rounded-xl shadow p-4 mb-4 flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[240px]">
            <label
              htmlFor="articles-search"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              חיפוש
            </label>
            <input
              id="articles-search"
              type="search"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="כותרת, תקציר או קטגוריה"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label
              htmlFor="articles-status"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              סטטוס
            </label>
            <select
              id="articles-status"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value as StatusFilter); setPage(1); }}
              className={selectClass}
            >
              <option value="all">הכל</option>
              <option value="published">מפורסם</option>
              <option value="draft">טיוטה</option>
            </select>
          </div>

          {categories.length > 0 && (
            <div>
              <label
                htmlFor="articles-category"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                קטגוריה
              </label>
              <select
                id="articles-category"
                value={categoryFilter}
                onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
                className={selectClass}
              >
                <option value="">כל הקטגוריות</option>
                {categories.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
          )}

          {isFiltered && (
            <button
              onClick={resetFilters}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              נקה סינון
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-500">טוען מאמרים...</div>
      ) : articles.length === 0 ? (
        <div className="text-center py-12 text-gray-500">אין מאמרים עדיין</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl shadow text-center py-12 text-gray-500">
          לא נמצאו מאמרים שמתאימים לסינון.{' '}
          <button onClick={resetFilters} className="text-blue-600 hover:underline">
            נקה סינון
          </button>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">כותרת</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">קטגוריה</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">סטטוס</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">תאריך</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((article) => (
                  <tr key={article.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-800">{article.title}</td>
                    <td className="px-4 py-3 text-gray-600">{article.category_name || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                        article.is_published
                          ? 'bg-green-100 text-green-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {article.is_published ? 'מפורסם' : 'טיוטה'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {new Date(article.ts).toLocaleDateString('he-IL')}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2 justify-end">
                        {article.is_published && article.slug ? (
                          <a
                            href={`/articles/${article.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1 text-gray-600 border border-gray-200 rounded hover:bg-gray-100 transition-colors"
                          >
                            צפה באתר
                          </a>
                        ) : (
                          <a
                            href={`/backoffice/articles/preview/${article.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1 text-gray-600 border border-gray-200 rounded hover:bg-gray-100 transition-colors"
                          >
                            תצוגה מקדימה
                          </a>
                        )}
                        <button
                          onClick={() => navigate(`/backoffice/articles/edit/${article.id}`)}
                          className="px-3 py-1 text-blue-600 border border-blue-200 rounded hover:bg-blue-50 transition-colors"
                        >
                          עריכה
                        </button>
                        <button
                          onClick={() => setDeleteConfirmId(article.id)}
                          className="px-3 py-1 text-red-600 border border-red-200 rounded hover:bg-red-50 transition-colors"
                        >
                          מחיקה
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* עימוד */}
          <div className="flex items-center justify-between gap-4 flex-wrap mt-4">
            <p className="text-sm text-gray-500">
              מציג {rangeStart}–{rangeEnd} מתוך {filtered.length}
              {isFiltered && ` (מסונן מתוך ${articles.length})`}
            </p>

            {totalPages > 1 && (
              <nav className="flex items-center gap-2" aria-label="עימוד מאמרים">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-transparent"
                >
                  הקודם
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    onClick={() => setPage(n)}
                    aria-current={n === page ? 'page' : undefined}
                    className={`px-3 py-1 text-sm border rounded-lg transition-colors ${
                      n === page
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {n}
                  </button>
                ))}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-transparent"
                >
                  הבא
                </button>
              </nav>
            )}
          </div>
        </>
      )}

      {/* דיאלוג אישור מחיקה */}
      {deleteConfirmId !== null && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4 shadow-xl" dir="rtl">
            <h2 className="text-lg font-bold text-gray-800 mb-3">אישור מחיקה</h2>
            <p className="text-gray-600 mb-6">האם למחוק את המאמר לצמיתות? לא ניתן לבטל פעולה זו.</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirmId(null)}
                disabled={deleting}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                ביטול
              </button>
              <button
                onClick={() => handleDelete(deleteConfirmId)}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'מוחק...' : 'מחק'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticlesListPage;
